import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Database, Download, Upload, RefreshCw, AlertTriangle, CheckCircle2,
  Clock, Trash2, RotateCcw, HardDrive, Image, FileText, Shield, Archive,
  Zap, Calendar, Settings, BarChart3, Wrench, Timer
} from 'lucide-react';

const BackupPage = () => {
  const [backups, setBackups] = useState([]);
  const [stats, setStats] = useState(null);
  const [dbStats, setDbStats] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [compacting, setCompacting] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [activeTab, setActiveTab] = useState('backups');
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [applyingRetention, setApplyingRetention] = useState(false);
  const fileInputRef = React.useRef(null);

  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    enabled: false,
    frequency: 'daily',
    hour: 2,
    minute: 0,
    retention_days: 30,
    max_backups: 10,
    day_of_week: 0,
  });

  useEffect(() => { fetchBackups(); fetchStats(); fetchSchedule(); }, []);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/backups');
      setBackups(response.data || []);
    } catch { toast.error('Eroare la incarcarea backup-urilor'); } finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/backups/stats');
      setStats(response.data);
    } catch {}
  };

  const fetchDbStats = async () => {
    try {
      const response = await api.get('/admin/db/stats');
      setDbStats(response.data);
    } catch { toast.error('Eroare la statisticile BD'); }
  };

  const fetchSchedule = async () => {
    try {
      const response = await api.get('/admin/backups/schedule');
      const data = response.data;
      setSchedule(data);
      setScheduleForm({
        enabled: data.enabled || false,
        frequency: data.frequency || 'daily',
        hour: data.hour ?? 2,
        minute: data.minute ?? 0,
        retention_days: data.retention_days ?? 30,
        max_backups: data.max_backups ?? 10,
        day_of_week: data.day_of_week ?? 0,
      });
    } catch {}
  };

  useEffect(() => {
    if (activeTab === 'optimize') fetchDbStats();
  }, [activeTab]);

  const handleCreateBackup = async () => {
    try {
      setCreating(true);
      toast.loading('Se creeaza backup complet (DB + fisiere)...', { id: 'create-backup' });
      const res = await api.post('/admin/backups/create');
      toast.success(
        `Backup complet creat! ${res.data.collections_count} colectii, ${res.data.files_count} fisiere (${formatFileSize(res.data.size)})`,
        { id: 'create-backup', duration: 6000 }
      );
      fetchBackups(); fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la crearea backup-ului', { id: 'create-backup' });
    } finally { setCreating(false); }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      toast.loading('Se descarca...', { id: 'download' });
      const response = await api.get(`/admin/backups/download/${filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', filename);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup descarcat!', { id: 'download' });
    } catch { toast.error('Eroare la descarcare', { id: 'download' }); }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm('Sigur doriti sa stergeti acest backup?')) return;
    try {
      await api.delete(`/admin/backups/${filename}`);
      toast.success('Backup sters!'); fetchBackups();
    } catch { toast.error('Eroare la stergere'); }
  };

  const handleRestoreBackup = async () => {
    if (!selectedBackup) return;
    try {
      setRestoring(true); setShowRestoreDialog(false);
      toast.loading('Se restaureaza backup-ul complet...', { id: 'restore' });
      const res = await api.post(`/admin/backups/restore/${selectedBackup.filename}`);
      toast.success(`Restaurare completa! ${res.data.collections_restored || 0} colectii, ${res.data.files_restored || 0} fisiere restaurate.`, { id: 'restore', duration: 6000 });
      setTimeout(() => window.location.reload(), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la restaurare', { id: 'restore' });
    } finally { setRestoring(false); }
  };

  const handleUploadBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.gz')) { toast.error('Fisierul trebuie sa fie .gz sau .tar.gz'); if (fileInputRef.current) fileInputRef.current.value = ''; return; }
    try {
      setUploading(true);
      toast.loading('Se incarca arhiva...', { id: 'upload' });
      const formData = new FormData(); formData.append('file', file);
      const res = await api.post('/admin/backups/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Arhiva incarcata: ${res.data.includes_files ? 'Backup complet' : 'Backup DB'}`, { id: 'upload', duration: 5000 });
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la incarcare', { id: 'upload' });
    } finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleCompact = async () => {
    if (!window.confirm('Compactarea poate dura cateva minute. Continuati?')) return;
    try {
      setCompacting(true);
      toast.loading('Se compacteaza baza de date...', { id: 'compact' });
      const res = await api.post('/admin/db/compact', {});
      const savedMB = (res.data.space_saved / (1024 * 1024)).toFixed(2);
      toast.success(`Compactare finalizata! ${res.data.compact_results?.length || 0} colectii, ${savedMB} MB recuperati`, { id: 'compact', duration: 6000 });
      fetchDbStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la compactare', { id: 'compact' });
    } finally { setCompacting(false); }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Se vor sterge: intrari stoc cu cantitate 0, audit-uri goale. Continuati?')) return;
    try {
      setCleaning(true);
      toast.loading('Se curata datele orfane...', { id: 'cleanup' });
      const res = await api.post('/admin/db/cleanup-orphans', {});
      const r = res.data.results || {};
      toast.success(`Curatare: ${r.zero_stock_removed || 0} stocuri zero sterse. ${r.old_movements_180d || 0} miscari vechi (>180 zile) gasite.`, { id: 'cleanup', duration: 6000 });
      fetchDbStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la curatare', { id: 'cleanup' });
    } finally { setCleaning(false); }
  };

  const handleSaveSchedule = async () => {
    try {
      setSavingSchedule(true);
      await api.post('/admin/backups/schedule', scheduleForm);
      toast.success('Program backup salvat!');
      fetchSchedule();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    } finally { setSavingSchedule(false); }
  };

  const handleApplyRetention = async () => {
    if (!window.confirm('Se vor sterge backup-urile vechi conform politicii de retentie. Continuati?')) return;
    try {
      setApplyingRetention(true);
      toast.loading('Se aplica politica de retentie...', { id: 'retention' });
      const res = await api.post('/admin/backups/apply-retention');
      toast.success(`${res.data.deleted?.length || 0} backup-uri sterse`, { id: 'retention', duration: 5000 });
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare', { id: 'retention' });
    } finally { setApplyingRetention(false); }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const getBackupIcon = (backup) => {
    if (backup.source === 'scheduled') return <Timer className="w-5 h-5 text-violet-600" />;
    if (backup.includes_files || backup.type === 'full_backup') return <Archive className="w-5 h-5 text-emerald-600" />;
    return <Database className="w-5 h-5 text-blue-600" />;
  };

  const getBackupBadge = (backup) => {
    if (backup.source === 'scheduled') return <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100">PROGRAMAT</Badge>;
    if (backup.includes_files || backup.type === 'full_backup' || backup.type === 'scheduled_backup') return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">COMPLET</Badge>;
    if (backup.type === 'mongodump') return <Badge variant="outline">mongodump</Badge>;
    if (backup.type === 'db_only' || backup.type === 'legacy') return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Doar DB</Badge>;
    return <Badge variant="outline">Legacy</Badge>;
  };

  const DAY_NAMES = ['Luni', 'Marti', 'Miercuri', 'Joi', 'Vineri', 'Sambata', 'Duminica'];

  return (
    <div className="space-y-6" data-testid="backup-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-emerald-600" />
            Backup & Optimizare BD
          </h1>
          <p className="text-slate-500 mt-1">Backup-uri, compactare, programare si statistici baza de date</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { fetchBackups(); fetchStats(); if (activeTab === 'optimize') fetchDbStats(); }} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Reincarca
          </Button>
          <Button onClick={handleCreateBackup} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="create-backup-btn">
            <Archive className="w-4 h-4 mr-2" />
            {creating ? 'Se creeaza...' : 'Backup Complet'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="backups" className="flex items-center gap-1.5"><Archive className="w-4 h-4" /> Backup-uri</TabsTrigger>
          <TabsTrigger value="optimize" className="flex items-center gap-1.5"><Zap className="w-4 h-4" /> Optimizare BD</TabsTrigger>
          <TabsTrigger value="schedule" className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Programare</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* ═══ TAB: BACKUPS ═══ */}
      {activeTab === 'backups' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Database className="w-5 h-5 text-blue-600" /></div><div><p className="text-xs text-slate-500 uppercase tracking-wider">Colectii DB</p><p className="text-xl font-bold">{stats?.db_collections || '-'}</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><Image className="w-5 h-5 text-amber-600" /></div><div><p className="text-xs text-slate-500 uppercase tracking-wider">Fisiere Upload</p><p className="text-xl font-bold">{stats?.uploaded_files || 0}</p><p className="text-xs text-slate-400">{formatFileSize(stats?.uploaded_files_size)}</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div><div><p className="text-xs text-slate-500 uppercase tracking-wider">Total Backup-uri</p><p className="text-xl font-bold">{backups.length}</p></div></div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><Clock className="w-5 h-5 text-purple-600" /></div><div><p className="text-xs text-slate-500 uppercase tracking-wider">Ultimul Backup</p><p className="text-sm font-medium">{stats?.last_backup ? new Date(stats.last_backup.created_at).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Niciodata'}</p></div></div></CardContent></Card>
          </div>

          {/* Info banner */}
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-emerald-600 mt-0.5" />
                <div className="text-sm space-y-1">
                  <p className="font-medium text-emerald-900">Ce include backup-ul complet?</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-emerald-700">
                    <span className="flex items-center gap-1.5"><Database className="w-3.5 h-3.5" /> Toate colectiile MongoDB</span>
                    <span className="flex items-center gap-1.5"><Image className="w-3.5 h-3.5" /> Imagini tichete si inchideri</span>
                    <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Documente revizii si unelte</span>
                  </div>
                  <p className="text-emerald-600 text-xs mt-1">Backup-urile sunt stocate si in MongoDB - supravietuiesc la redeploy!</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upload */}
          <Card className="border-indigo-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg"><Upload className="w-5 h-5 text-indigo-600" /> Restaurare din Arhiva Externa</CardTitle>
              <CardDescription>Incarcati o arhiva backup (.tar.gz sau .gz) pentru a o restaura</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <input ref={fileInputRef} type="file" accept=".gz,.tar.gz" onChange={handleUploadBackup} className="hidden" data-testid="backup-file-input" />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="upload-backup-btn">
                  <Upload className={`w-4 h-4 mr-2 ${uploading ? 'animate-bounce' : ''}`} />
                  {uploading ? 'Se incarca...' : 'Incarca Arhiva'}
                </Button>
                <p className="text-xs text-slate-500">Accepta backup-uri complete (.tar.gz) sau MongoDB (.gz)</p>
              </div>
            </CardContent>
          </Card>

          {/* Backup List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div><CardTitle>Istoric Backup-uri</CardTitle><CardDescription>Toate backup-urile disponibile</CardDescription></div>
                <Button variant="outline" size="sm" onClick={handleApplyRetention} disabled={applyingRetention} className="text-amber-600 border-amber-200 hover:bg-amber-50">
                  <Trash2 className="w-4 h-4 mr-1" /> {applyingRetention ? 'Se aplica...' : 'Aplica Retentie'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8"><RefreshCw className="w-8 h-8 mx-auto text-slate-400 animate-spin mb-2" /><p className="text-slate-500">Se incarca...</p></div>
              ) : backups.length === 0 ? (
                <div className="text-center py-12"><Archive className="w-16 h-16 mx-auto text-slate-200 mb-4" /><p className="text-slate-500 font-medium">Nu exista backup-uri</p></div>
              ) : (
                <div className="space-y-3">
                  {backups.map((backup, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50/50 transition-all" data-testid={`backup-row-${idx}`}>
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">{getBackupIcon(backup)}</div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-slate-900 truncate">{backup.filename}</p>
                            {getBackupBadge(backup)}
                            {backup.source === 'upload' && <Badge variant="outline" className="text-xs text-indigo-600 border-indigo-200">Incarcat</Badge>}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(backup.created_at).toLocaleString('ro-RO')}</span>
                            <span className="flex items-center gap-1"><HardDrive className="w-3 h-3" />{formatFileSize(backup.size)}</span>
                            {backup.collections_count > 0 && <span>{backup.collections_count} colectii</span>}
                            {backup.files_count > 0 && <span className="text-emerald-600 font-medium">{backup.files_count} fisiere</span>}
                            {backup.created_by && <span>de {backup.created_by.split('@')[0]}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <Button variant="outline" size="sm" onClick={() => handleDownloadBackup(backup.filename)}><Download className="w-4 h-4 mr-1" /> Download</Button>
                        <Button variant="outline" size="sm" onClick={() => { setSelectedBackup(backup); setShowRestoreDialog(true); }} className="text-amber-600 hover:bg-amber-50 border-amber-200"><RotateCcw className="w-4 h-4 mr-1" /> Restore</Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteBackup(backup.filename)} className="text-red-500 hover:bg-red-50 border-red-200"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ═══ TAB: OPTIMIZE ═══ */}
      {activeTab === 'optimize' && (
        <>
          {/* Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-blue-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg"><Zap className="w-5 h-5 text-blue-600" /> Compactare BD</CardTitle>
                <CardDescription>Compacteaza colectiile si reconstruieste indecsi pentru a recupera spatiu</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={handleCompact} disabled={compacting} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="compact-btn">
                  <Zap className={`w-4 h-4 mr-2 ${compacting ? 'animate-pulse' : ''}`} />
                  {compacting ? 'Se compacteaza...' : 'Compacteaza Acum'}
                </Button>
                <p className="text-xs text-slate-500 mt-2">Recomandat dupa stergeri masive sau importuri mari de date</p>
              </CardContent>
            </Card>

            <Card className="border-amber-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg"><Wrench className="w-5 h-5 text-amber-600" /> Curatare Date Orfane</CardTitle>
                <CardDescription>Sterge intrari stoc cu cantitate 0 si alte date orfane</CardDescription>
              </CardHeader>
              <CardContent>
                <Button onClick={handleCleanup} disabled={cleaning} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="cleanup-btn">
                  <Wrench className={`w-4 h-4 mr-2 ${cleaning ? 'animate-spin' : ''}`} />
                  {cleaning ? 'Se curata...' : 'Curatare Orfane'}
                </Button>
                <p className="text-xs text-slate-500 mt-2">Sterge stocuri zero, audit-uri goale</p>
              </CardContent>
            </Card>
          </div>

          {/* DB Stats Summary */}
          {dbStats?.summary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card><CardContent className="p-3 text-center"><p className="text-xs text-slate-500">Colectii</p><p className="text-xl font-bold text-slate-900">{dbStats.summary.total_collections}</p></CardContent></Card>
              <Card><CardContent className="p-3 text-center"><p className="text-xs text-slate-500">Documente</p><p className="text-xl font-bold text-slate-900">{dbStats.summary.total_documents?.toLocaleString()}</p></CardContent></Card>
              <Card><CardContent className="p-3 text-center"><p className="text-xs text-slate-500">Date</p><p className="text-xl font-bold text-blue-600">{formatFileSize(dbStats.summary.total_data_size)}</p></CardContent></Card>
              <Card><CardContent className="p-3 text-center"><p className="text-xs text-slate-500">Stocare</p><p className="text-xl font-bold text-amber-600">{formatFileSize(dbStats.summary.total_storage_size)}</p></CardContent></Card>
              <Card><CardContent className="p-3 text-center"><p className="text-xs text-slate-500">Indecsi</p><p className="text-xl font-bold text-emerald-600">{formatFileSize(dbStats.summary.total_index_size)}</p></CardContent></Card>
            </div>
          )}

          {/* Collection Details */}
          {dbStats?.collections && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-slate-600" /> Detalii Colectii</CardTitle>
                <CardDescription>Dimensiune, documente si fragmentare per colectie (ordonate dupa marime)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-2 font-medium text-slate-500">Colectie</th>
                        <th className="pb-2 font-medium text-slate-500 text-right">Documente</th>
                        <th className="pb-2 font-medium text-slate-500 text-right">Dimensiune</th>
                        <th className="pb-2 font-medium text-slate-500 text-right">Stocare</th>
                        <th className="pb-2 font-medium text-slate-500 text-right">Indecsi</th>
                        <th className="pb-2 font-medium text-slate-500 text-right">Dim. Medie Doc</th>
                        <th className="pb-2 font-medium text-slate-500 text-right">Fragmentare</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dbStats.collections.map((col) => (
                        <tr key={col.name} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 font-mono text-xs">{col.name}</td>
                          <td className="py-2 text-right">{col.documents.toLocaleString()}</td>
                          <td className="py-2 text-right">{formatFileSize(col.size)}</td>
                          <td className="py-2 text-right">{formatFileSize(col.storage_size)}</td>
                          <td className="py-2 text-right">{formatFileSize(col.index_size)}</td>
                          <td className="py-2 text-right">{formatFileSize(col.avg_doc_size)}</td>
                          <td className="py-2 text-right">
                            <span className={`${col.fragmentation_pct > 50 ? 'text-red-600 font-medium' : col.fragmentation_pct > 20 ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {col.fragmentation_pct}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ═══ TAB: SCHEDULE ═══ */}
      {activeTab === 'schedule' && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-violet-600" /> Backup Programat</CardTitle>
              <CardDescription>Configurati backup-uri automate cu politica de retentie</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 rounded-lg border bg-slate-50">
                <div>
                  <p className="font-medium">Backup Automat</p>
                  <p className="text-sm text-slate-500">Creeaza backup-uri automat conform programului</p>
                </div>
                <Switch
                  checked={scheduleForm.enabled}
                  onCheckedChange={(v) => setScheduleForm(prev => ({...prev, enabled: v}))}
                  data-testid="schedule-enabled-switch"
                />
              </div>

              {scheduleForm.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Frequency */}
                  <div className="space-y-2">
                    <Label>Frecventa</Label>
                    <Select value={scheduleForm.frequency} onValueChange={(v) => setScheduleForm(prev => ({...prev, frequency: v}))}>
                      <SelectTrigger data-testid="schedule-frequency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Zilnic</SelectItem>
                        <SelectItem value="weekly">Saptamanal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Day of week (for weekly) */}
                  {scheduleForm.frequency === 'weekly' && (
                    <div className="space-y-2">
                      <Label>Ziua saptamanii</Label>
                      <Select value={String(scheduleForm.day_of_week)} onValueChange={(v) => setScheduleForm(prev => ({...prev, day_of_week: parseInt(v)}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DAY_NAMES.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Hour */}
                  <div className="space-y-2">
                    <Label>Ora (UTC)</Label>
                    <Select value={String(scheduleForm.hour)} onValueChange={(v) => setScheduleForm(prev => ({...prev, hour: parseInt(v)}))}>
                      <SelectTrigger data-testid="schedule-hour"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.from({length: 24}, (_, i) => (
                          <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}:00</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Retention Days */}
                  <div className="space-y-2">
                    <Label>Retentie (zile)</Label>
                    <Input
                      type="number" min={1} max={365}
                      value={scheduleForm.retention_days}
                      onChange={(e) => setScheduleForm(prev => ({...prev, retention_days: parseInt(e.target.value) || 30}))}
                      data-testid="schedule-retention-days"
                    />
                    <p className="text-xs text-slate-500">Backup-urile mai vechi se sterg automat</p>
                  </div>

                  {/* Max Backups */}
                  <div className="space-y-2">
                    <Label>Numar maxim backup-uri</Label>
                    <Input
                      type="number" min={1} max={100}
                      value={scheduleForm.max_backups}
                      onChange={(e) => setScheduleForm(prev => ({...prev, max_backups: parseInt(e.target.value) || 10}))}
                      data-testid="schedule-max-backups"
                    />
                    <p className="text-xs text-slate-500">Cele mai vechi se sterg cand se depaseste limita</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <Button onClick={handleSaveSchedule} disabled={savingSchedule} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="save-schedule-btn">
                  <Settings className="w-4 h-4 mr-2" />
                  {savingSchedule ? 'Se salveaza...' : 'Salveaza Program'}
                </Button>
                {schedule?.last_scheduled_run && (
                  <p className="text-sm text-slate-500">
                    Ultimul backup automat: {new Date(schedule.last_scheduled_run).toLocaleString('ro-RO')}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Schedule Status */}
          <Card className={schedule?.enabled ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200'}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                {schedule?.enabled ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                )}
                <div>
                  <p className="font-medium">
                    {schedule?.enabled
                      ? `Backup automat ACTIV - ${schedule.frequency === 'daily' ? 'Zilnic' : `Saptamanal (${DAY_NAMES[schedule.day_of_week || 0]})`} la ${String(schedule.hour || 0).padStart(2, '0')}:00 UTC`
                      : 'Backup automat DEZACTIVAT'
                    }
                  </p>
                  {schedule?.enabled && (
                    <p className="text-sm text-slate-500">
                      Retentie: {schedule.retention_days} zile, max {schedule.max_backups} backup-uri
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Restore Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Restaurare Backup</DialogTitle>
            <DialogDescription>Aceasta operatiune va INLOCUI toate datele curente!</DialogDescription>
          </DialogHeader>
          {selectedBackup && (
            <div className="space-y-4">
              <div className="bg-slate-50 border p-4 rounded-lg space-y-2">
                <div className="flex items-center gap-2">{getBackupIcon(selectedBackup)}<span className="font-medium">{selectedBackup.filename}</span>{getBackupBadge(selectedBackup)}</div>
                <div className="text-sm text-slate-600 grid grid-cols-2 gap-2">
                  <span>Data: {new Date(selectedBackup.created_at).toLocaleString('ro-RO')}</span>
                  <span>Marime: {formatFileSize(selectedBackup.size)}</span>
                  {selectedBackup.collections_count > 0 && <span>Colectii: {selectedBackup.collections_count}</span>}
                  {selectedBackup.files_count > 0 && <span>Fisiere: {selectedBackup.files_count}</span>}
                </div>
              </div>
              <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                <p className="text-sm font-medium text-red-900">Ce se va intampla:</p>
                <ul className="text-xs text-red-700 mt-2 space-y-1">
                  <li>Toate colectiile MongoDB vor fi inlocuite</li>
                  {selectedBackup.includes_files && <li>Fisierele uploadate vor fi restaurate</li>}
                  <li className="font-semibold">Operatiunea NU poate fi anulata!</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRestoreDialog(false)}>Anuleaza</Button>
            <Button onClick={handleRestoreBackup} disabled={restoring} className="bg-red-600 hover:bg-red-700 text-white">
              <RotateCcw className="w-4 h-4 mr-2" /> Confirma Restaurare
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BackupPage;
